import { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, ModalContent } from '@nextui-org/react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { Check, ChevronLeft, X } from 'lucide-react';

import { formatQuantity, numberFormatter, planFoodToEntry, sumEntries } from '../../utils/calorieLog';
import { MAX_GRAMS } from '../../utils/foodEdit';
import { foodKey, searchedFoodName, useFoodSearch } from '../../utils/foodSearch';
import { AmountPicker, FoodFacts, FoodName, FoodResults, FoodSearchField } from './FoodPicker';
import '../../styles/pages/calorias.css';

const DEFAULT_GRAMS = 100;

// Search results are per 100 g; scale them to the amount eaten
const scale = (value, grams) => Math.round(((value || 0) * grams) / 100);

/**
 * What's inside the sheet. It mounts on every opening, so each one starts with an empty selection.
 *
 * One selection holds foods from both sources: planned foods are checked in "Do plano", searched foods are weighed
 * and included from "Buscar alimento", and the footer adds all of them at once.
 */
const SheetContent = ({ meal, dayName, initialTab, onAdd, isSaving, close }) => {
  const { t, i18n: { language } } = useTranslation();
  const ui = (key, options) => t(`pages.CalorieTracker.ui.${key}`, options);
  const isDesktop = useMediaQuery({ query: '(min-width: 960px)' });
  const locale = language === 'pt' ? 'pt-BR' : 'en-US';
  const formatNumber = numberFormatter(language);

  const planFoods = useMemo(() => (meal?.foods || []).filter((food) => food.name), [meal]);
  const hasPlan = planFoods.length > 0;

  const [tab, setTab] = useState(hasPlan && initialTab !== 'search' ? 'plan' : 'search');
  // The selection: indexes of the checked planned foods, and the searched foods with their grams
  const [checked, setChecked] = useState(() => new Set());
  const [picks, setPicks] = useState([]);
  const [query, setQuery] = useState('');
  const { results, isSearching, hasSearched } = useFoodSearch(query, language);
  // The searched food being weighed (a new one, or one already in the selection)
  const [weighing, setWeighing] = useState(null);
  const [grams, setGrams] = useState(String(DEFAULT_GRAMS));

  const searchRef = useRef(null);
  const amountTitleRef = useRef(null);
  const gramsRef = useRef(null);
  const addRef = useRef(null);
  // Where the focus goes once the next view has rendered
  const focusNext = useRef(null);

  useEffect(() => {
    const target = focusNext.current;
    if (!target) return;
    focusNext.current = null;
    if (target === 'search') searchRef.current?.focus();
    // Desktop types the grams; phones get the food read out and use the slider (no keyboard over it)
    if (target === 'amount') {
      if (isDesktop) {
        gramsRef.current?.focus();
        gramsRef.current?.select();
      } else {
        amountTitleRef.current?.focus();
      }
    }
    if (target === 'list') (isDesktop ? searchRef : addRef).current?.focus();
  });

  const foodName = (food) => searchedFoodName(food, language);
  const searchedEntry = (food, amount) => ({
    name: foodName(food),
    quantity: `${amount} g`,
    calories: scale(food.calories, amount),
    protein: scale(food.protein, amount),
    carbs: scale(food.carbs, amount),
    fat: scale(food.fat, amount),
  });

  const selection = [
    ...planFoods.flatMap((food, index) => (checked.has(index) ? [planFoodToEntry(food)] : [])),
    ...picks.map(({ food, grams: amount }) => searchedEntry(food, amount)),
  ];
  const selectionKcal = sumEntries(selection).calories;
  const pickByKey = (key) => picks.find((pick) => pick.key === key);

  const amount = Number(grams);
  const isAmountValid = Number.isFinite(amount) && amount > 0 && amount <= MAX_GRAMS;
  const formatGrams = (value) => value.toLocaleString(locale, { maximumFractionDigits: 1 });

  const switchTab = (next) => {
    setTab(next);
    setWeighing(null);
    if (next === 'search') focusNext.current = 'search';
  };

  const toggleChecked = (index) =>
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });

  const isAllChecked = checked.size === planFoods.length;
  const toggleAll = () => setChecked(isAllChecked ? new Set() : new Set(planFoods.map((_, index) => index)));

  const weigh = (food) => {
    const key = foodKey(food);
    const pick = pickByKey(key);
    setWeighing({ food, key, isEditing: !!pick });
    setGrams(String(pick ? pick.grams : DEFAULT_GRAMS));
    focusNext.current = 'amount';
  };

  const backToList = () => {
    setWeighing(null);
    focusNext.current = 'list';
  };

  // In the selection, or its grams changed: an edited food keeps its place
  const includeWeighed = () => {
    const pick = { key: weighing.key, food: weighing.food, grams: amount };
    setPicks((prev) => (prev.some((item) => item.key === pick.key) ? prev.map((item) => (item.key === pick.key ? pick : item)) : [...prev, pick]));
    backToList();
  };

  const removePick = (key) => {
    setPicks((prev) => prev.filter((pick) => pick.key !== key));
    if (weighing?.key === key) backToList();
  };

  const renderPlan = () => (
    <>
      <div className="mt-cal-sheet__allrow">
        <span>{ui('markWhatYouAte')}</span>
        <button type="button" className="mt-cal-sheet__all" onClick={toggleAll}>
          {isAllChecked ? ui('uncheckAll') : ui('checkAll')}
        </button>
      </div>
      <ul className="mt-cal-picks">
        {planFoods.map((food, index) => (
          <li key={`${index}-${food.name}`}>
            <button
              type="button"
              role="checkbox"
              aria-checked={checked.has(index)}
              className="mt-cal-pick mt-cal-pick--check"
              onClick={() => toggleChecked(index)}
            >
              <span className="mt-cal-box" aria-hidden="true">
                <Check />
              </span>
              <span className="mt-cal-pick__text">
                <span className="mt-cal-pick__name">{food.name}</span>
                {food.quantity && <span className="mt-cal-pick__meta">{formatQuantity(food.quantity)}</span>}
              </span>
              <span className="mt-cal-pick__kcal">{ui('kcal', { kcal: formatNumber(food.calories) })}</span>
            </button>
          </li>
        ))}
      </ul>
    </>
  );

  // The searched foods already in the selection: tap one to change its grams, or take it out
  const renderPicks = () => (
    <section className="mt-cal-sheet__picked" aria-labelledby="mt-cal-picked-title">
      <h3 id="mt-cal-picked-title" className="mt-cal-sheet__allrow">
        {ui('selectionTitle')}
      </h3>
      <ul className="mt-cal-picks">
        {picks.map(({ key, food, grams: pickGrams }) => (
          <li key={key} className="mt-cal-picked">
            <button type="button" className="mt-cal-pick" onClick={() => weigh(food)}>
              <span className="mt-cal-pick__text">
                <span className="mt-cal-pick__name">
                  <FoodName name={foodName(food)} />
                </span>
                <span className="mt-cal-pick__meta">{`${formatGrams(pickGrams)} g`}</span>
              </span>
              <span className="mt-cal-pick__kcal">{ui('kcal', { kcal: formatNumber(scale(food.calories, pickGrams)) })}</span>
            </button>
            <button type="button" className="mt-cal-del" onClick={() => removePick(key)} aria-label={ui('removePickNamed', { name: foodName(food) })}>
              <X aria-hidden="true" />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );

  const renderSearch = () => {
    if (!query.trim()) {
      return (
        <>
          {picks.length > 0 && renderPicks()}
          <p className="mt-cal-sheet__msg">{ui('searchHint')}</p>
        </>
      );
    }
    return (
      <FoodResults
        results={results}
        isSearching={isSearching}
        hasSearched={hasSearched}
        onPick={weigh}
        pickedGrams={(food) => pickByKey(foodKey(food))?.grams}
      />
    );
  };

  const renderAmount = () => {
    const { food, isEditing } = weighing;
    return (
      <div className="mt-cal-amount">
        <div className="mt-cal-amount__top">
          <button type="button" className="mt-cal-link" onClick={backToList}>
            <ChevronLeft aria-hidden="true" />
            {ui('backToSearch')}
          </button>
          {isEditing && (
            <button type="button" className="mt-cal-link mt-cal-link--quiet" onClick={() => removePick(weighing.key)}>
              {ui('removePick')}
            </button>
          )}
        </div>
        <h3 className="mt-cal-amount__name" ref={amountTitleRef} tabIndex={-1}>
          <FoodName name={foodName(food)} />
        </h3>
        <p className="mt-cal-amount__per">{ui('kcalPer100', { kcal: formatNumber(food.calories) })}</p>
        <AmountPicker value={grams} onChange={setGrams} inputRef={gramsRef} limit={MAX_GRAMS} />
        <FoodFacts
          values={
            isAmountValid && {
              calories: scale(food.calories, amount),
              protein: scale(food.protein, amount),
              carbs: scale(food.carbs, amount),
              fat: scale(food.fat, amount),
            }
          }
        />
      </div>
    );
  };

  let body;
  if (weighing) body = renderAmount();
  else if (tab === 'plan') body = renderPlan();
  else body = renderSearch();

  let footer = null;
  if (weighing) {
    footer = (
      <button type="button" className="mt-btn mt-btn--primary mt-cal-btn-big" onClick={includeWeighed} disabled={!isAmountValid}>
        {ui(weighing.isEditing ? 'updatePick' : 'includePick', { grams: isAmountValid ? formatGrams(amount) : '–' })}
      </button>
    );
  } else if (selection.length > 0) {
    footer = (
      <button
        ref={addRef}
        type="button"
        className="mt-btn mt-btn--primary mt-cal-btn-big"
        onClick={() => onAdd(selection)}
        disabled={isSaving}
        aria-busy={isSaving || undefined}
      >
        {isSaving && <span className="mt-btn__spinner" aria-hidden="true" />}
        {ui('addSelection', { count: selection.length, kcal: formatNumber(selectionKcal) })}
      </button>
    );
  } else if (tab === 'plan') {
    footer = (
      <button type="button" className="mt-btn mt-btn--primary mt-cal-btn-big" disabled>
        {ui('markWhatYouAte')}
      </button>
    );
  }

  return (
    <>
      <div className="mt-cal-sheet__head">
        <span className="mt-cal-sheet__grab" aria-hidden="true" />
        <div className="mt-cal-sheet__top">
          <div>
            <h2 id="mt-cal-sheet-title" className="mt-cal-sheet__title">
              {meal?.name}
            </h2>
            <p className="mt-cal-sheet__sub">{ui('sheetSub', { day: dayName })}</p>
          </div>
          <button type="button" className="mt-icon-btn" onClick={close} aria-label={t('standalone.close')}>
            <X aria-hidden="true" />
          </button>
        </div>
        {hasPlan && (
          <div className="mt-segment mt-cal-sheet__tabs" role="group" aria-label={ui('source')}>
            <button type="button" aria-pressed={tab === 'plan'} onClick={() => switchTab('plan')}>
              {ui('tabPlan')}
              {checked.size > 0 && <span className="mt-cal-count">{checked.size}</span>}
            </button>
            <button type="button" aria-pressed={tab === 'search'} onClick={() => switchTab('search')}>
              {ui('tabSearch')}
              {picks.length > 0 && <span className="mt-cal-count">{picks.length}</span>}
            </button>
          </div>
        )}
        {tab === 'search' && !weighing && <FoodSearchField inputRef={searchRef} value={query} onChange={setQuery} />}
      </div>
      <div className="mt-cal-sheet__body">{body}</div>
      {footer && <div className="mt-cal-sheet__foot">{footer}</div>}
    </>
  );
};

/**
 * Sheet for logging foods into one meal (a bottom sheet on phones, a dialog on desktop): check foods from the plan,
 * search the food database and weigh them, in any mix, then add them all at once.
 */
export const AddFoodSheet = ({ isOpen, onOpenChange, meal, dayName, initialTab = 'plan', onAdd, isSaving }) => {
  const isDesktop = useMediaQuery({ query: '(min-width: 960px)' });

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      placement={isDesktop ? 'center' : 'bottom'}
      hideCloseButton
      classNames={{ base: 'mt-root mt-cal-sheet', backdrop: 'mt-cal-backdrop' }}
    >
      <ModalContent aria-labelledby="mt-cal-sheet-title">
        {(close) => <SheetContent meal={meal} dayName={dayName} initialTab={initialTab} onAdd={onAdd} isSaving={isSaving} close={close} />}
      </ModalContent>
    </Modal>
  );
};
